/**
 * One pattern for holding an edit apart from the committed value.
 *
 * The prototype has three — `S.draft`, `S.promptDraft` and mutable fields on proposal changes —
 * and discards all of them silently on navigation. This one tracks dirtiness so a screen can
 * prompt before leaving.
 *
 * The baseline lives in state beside the draft rather than in a ref, so `dirty` is a value
 * derived during render rather than a mutable read.
 */

import { useCallback, useState } from 'react';

export interface Draft<T> {
  draft: T;
  dirty: boolean;
  update: (patch: Partial<T> | ((current: T) => T)) => void;
  replace: (next: T) => void;
  revert: () => void;
  /** Adopt a new saved value, discarding the draft — used after a successful write. */
  rebase: (saved: T) => void;
}

interface Held<T> {
  draft: T;
  baseline: T;
}

export function useDraft<T extends object>(saved: T): Draft<T> {
  const [held, setHeld] = useState<Held<T>>({ draft: saved, baseline: saved });

  const update = useCallback((patch: Partial<T> | ((current: T) => T)) => {
    setHeld((current) => ({
      ...current,
      draft:
        typeof patch === 'function'
          ? (patch as (value: T) => T)(current.draft)
          : { ...current.draft, ...patch },
    }));
  }, []);

  const replace = useCallback((next: T) => {
    setHeld((current) => ({ ...current, draft: next }));
  }, []);

  const revert = useCallback(() => {
    setHeld((current) => ({ ...current, draft: current.baseline }));
  }, []);

  const rebase = useCallback((next: T) => {
    setHeld({ draft: next, baseline: next });
  }, []);

  return {
    draft: held.draft,
    dirty: JSON.stringify(held.draft) !== JSON.stringify(held.baseline),
    update,
    replace,
    revert,
    rebase,
  };
}
